import Swal from "sweetalert2";
import { CaretDownFilled } from "@ant-design/icons"
import { faClose, faExternalLink, faKey } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Form, Modal, Select, Row, Col, Input, Tag, DatePicker} from "antd";
import React, { useCallback, useRef, useState } from "react"
import { getPayLevelQuery } from "@src/util/query/paylevel.query";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { getSitesQuery } from "@src/util/query/site.query";
import debounce from 'lodash/debounce';
import Spinner from "@src/components/spinner";
import { getAgencies } from "@src/util/query/agency.query";
import { GetEmployeeRecord } from "@src/util/query/employee.query";
import { getReportTemplates } from '@src/util/query/reportTemplate.query';
import { useEffect } from "react";
import { newMobileResponseMutation } from "@src/util/mutation/mobileServices.mutation";
import { useDispatch } from "react-redux";
import { setNotification } from '@src/middleware/redux/reducers/notification.reducer';
import Checkbox from "antd/es/checkbox/Checkbox";
import dayjs from "dayjs";
import { GetKeyCurrentCustodianBySiteIdQuery } from "@src/util/query/keyRegister.query";
import { getSiteQuery, GetSiteKeysSummaryQuery } from "@src/util/query/site.query";
import { UpdateSingleSiteMutation } from "@src/util/mutation/site.mutation";
import { NewKeyModal } from "../keyRegister/newKey.modal";
import { HelpLink } from "@src/components/helpLink/helpLink";
import { directMobileDriversQuery, mobileReportFormsQuery } from "@src/util/query/mobileOfficer.query";



export const NewMobileResponseModal = ({
  visible, close, refetch,
  prefillSiteId = null,
  prefillPriority = null,
  prefillDutyTypeId = null,
  prefillStartTime = null,
  prefillEndTime = null,
  prefillNotes = null,
  prefillScheduleType = null,
  prefillScheduleDays = null,
  prefillFrequencyDays = null,
}) => {
  const [form] = Form.useForm();
  const { data: reportFormsData } = useQuery(mobileReportFormsQuery, { fetchPolicy: "cache-and-network" });
  const { Option, OptGroup } = Select;

  const [scheduleDays, setScheduleDays] = useState([]);
  const [frequencyDays, setFrequencyDays] = useState(0);
  const [lockedEmployee, setLockedEmployee] = useState(null);
  // In-House: the direct-jobs mobile drivers (shift runners get jobs on their shift - the key
  // holder at Clock In, or a transfer)
  const { data: directDriversData, loading: directDriversLoading } = useQuery(directMobileDriversQuery, { fetchPolicy: 'cache-and-network' });
  const [prefillSiteOption, setPrefillSiteOption] = useState(null); // {value, label, postcode}

  const [newMobileResponse, {loading: newMobileResponseLoading}] = useMutation(newMobileResponseMutation);

  const [fetchPrefillSite] = useLazyQuery(getSiteQuery, { fetchPolicy: "network-only" });

  // "Does this site have a key on file?" soft check on Save - see handleSave. Never blocks the
  // save either way, just nudges toward logging one and (if the answer is "no") remembers that
  // for next time so it doesn't nag on every duty.
  const [fetchSiteKeysSummary] = useLazyQuery(GetSiteKeysSummaryQuery, { fetchPolicy: "network-only" });
  const [acknowledgeNoKey] = useMutation(UpdateSingleSiteMutation);
  const [postSaveKeyCheckSite, setPostSaveKeyCheckSite] = useState(null); // {_id, sitename} | null

  // Non-site fields: set immediately on open, no async needed
  useEffect(() => {
    const fields = {};
    if (prefillPriority)     fields.priority     = prefillPriority;
    if (prefillDutyTypeId)   fields.dutyTypeId   = prefillDutyTypeId;
    if (prefillScheduleType) fields.scheduleType = prefillScheduleType;
    if (prefillNotes)        fields.notes        = prefillNotes;
    if (prefillStartTime) {
      const parsed = dayjs(prefillStartTime, "DD/MM/YYYY HH:mm");
      if (parsed.isValid()) fields.startTime = parsed;
    }
    if (prefillEndTime) {
      const parsed = dayjs(prefillEndTime, "DD/MM/YYYY HH:mm");
      if (parsed.isValid()) fields.endTime = parsed;
    }
    if (Object.keys(fields).length > 0) form.setFieldsValue(fields);

    // UFN schedule state (not form fields — used directly in the save payload)
    if (prefillScheduleDays?.length > 0) setScheduleDays(prefillScheduleDays);
    if (prefillFrequencyDays > 0)        setFrequencyDays(prefillFrequencyDays);
  }, [prefillPriority, prefillDutyTypeId, prefillScheduleType, prefillNotes, prefillStartTime, prefillEndTime, prefillScheduleDays, prefillFrequencyDays]);

  // Site field: requires async fetch to build the JSON value the Select expects
  useEffect(() => {
    if (!prefillSiteId) return;
    (async () => {
      try {
        const { data } = await fetchPrefillSite({ variables: { id: prefillSiteId } });
        const s = data?.site?.response;
        if (!s) return;
        const siteValue = JSON.stringify({
          siteId: s._id,
          customerId: s.customerDetail?._id || null,
          supplierId: s.metadata?.supplierId || null,
          currentsupplier: s.metadata?.currentsupplier || null,
        });
        setPrefillSiteOption({ value: siteValue, label: s.sitename, postcode: s.postcode });
        form.setFieldsValue({ siteId: siteValue });
      } catch (e) {
        console.error("Prefill site fetch failed", e);
      }
    })();
  }, [prefillSiteId]);


  const dispatch = useDispatch();

  // Site has zero AllKeys records and hasn't already told us "no key" before - ask, but this is a
  // soft check: whatever the answer, the duty still gets saved right after. "Yes" opens the New Key
  // form prefilled to this site (as a follow-up, once this modal's own save/close has gone
  // through); "No" is remembered on the site itself so this doesn't ask again next duty - only
  // actually adding a key anywhere clears that, by making the hasKey check above short-circuit.
  const runKeyCheck = async (selectedSiteId) => {
    if(!selectedSiteId) return;
    try{
      const [{data: keysData}, {data: siteData}] = await Promise.all([
        fetchSiteKeysSummary({variables: {siteId: selectedSiteId}}),
        fetchPrefillSite({variables: {id: selectedSiteId}}),
      ]);
      const hasKey = (keysData?.getAllKeys?.response?.length || 0) > 0;
      const selectedSite = siteData?.site?.response;
      if(hasKey || selectedSite?.noKeyAcknowledged) return;

      const result = await Swal.fire({
        icon: "question",
        title: "Do you have a key for this site yet?",
        text: `No key has been registered for ${selectedSite?.sitename || "this site"}.`,
        showCancelButton: true,
        confirmButtonText: "Yes, I have it",
        cancelButtonText: "No, I don't have it",
        confirmButtonColor: "#0D5C63",
        cancelButtonColor: "#6c757d",
      });

      if(result.isConfirmed){
        setPostSaveKeyCheckSite({_id: selectedSiteId, sitename: selectedSite?.sitename});
      }else if(result.dismiss === Swal.DismissReason.cancel){
        await acknowledgeNoKey({variables:{input:{_id: selectedSiteId, properties:[{name: "noKeyAcknowledged", value: true}]}}});
      }
    }catch(e){
      console.error("Key check failed", e);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const selectedSiteId = JSON.parse(values?.siteId)?.siteId;

      await runKeyCheck(selectedSiteId);

      await newMobileResponse({
        variables: {
          input:{
            ...values,
            siteId: selectedSiteId,
            status:( values?.employeeId || values?.supplierId) ? "onschedule" : "Open",
            frequencyDays: scheduleType=="ufn"? frequencyDays : "",
            scheduleDays: scheduleType=="ufn"? scheduleDays : ""
          }
        }
      });

      dispatch(setNotification({
        error: false,
        notificationState: true,
        message: "New response was added successfully"
      }));

      form.resetFields();

      close();

      refetch();


      // call your mutation / API here
    } catch (err) {
      // AntD's own form.validateFields() rejection has no `.message` (it's an
      // {errorFields} object - AntD already shows those inline). A real
      // mutation rejection - e.g. the site-termination duty lockout below -
      // does have one, and needs to actually reach the user, not just the
      // console.
      if (err?.message) {
        dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
      } else {
        console.log("Validation error:", err);
      }
    }
  };

  const {data: dutyTypeData, loading: dutyTypeLoading} = useQuery(getPayLevelQuery,{
    fetchPolicy: 'network-only',
  });

  const [getSites ,{data: siteData, loading:siteLoading}] = useLazyQuery(getSitesQuery);


  const handelSiteSearch = async (input) => {
    await getSites({
      variables: {
        input: {
          // filters: {
          //   advanceFilter: [
          //     [{
          //       operator: "sitename",
          //       condition: "and",
          //       filter: "contain_exactly",
          //       filterValue: [input]
          //     }]
          //   ]
          // },
          search: input,
          page: 1,
          pageSize: 5,
        }
      },
      fetchPolicy: 'cache-and-network'
    });
    
    
  };
  
  // Debounced version
  const debouncedSiteSearch = useCallback(
    debounce((input) => {
      handelSiteSearch(input);
    }, 500), // 500ms delay
    [] // dependencies
  );

  const empPay = {
    employee: "Employee Pay Rate",
    site: "Site Pay Rate",
    sitegroup: "SiteGroup Pay Rate",
    customer: "Customer Pay Rate"
  };

  const supplierPay = {
    site: "Site Pay Rate",
    sitegroup: "SiteGroup Pay Rate",
    supplier: "Supplier Pay Rate",
  };
  
  const billRate = {
    site: "Site Bill Rate",
    customer: "Customer Bill Rate",
  };
  




  const [getAgencyQuery, {data: agencyData, loading: agencyLoading}] = useLazyQuery(getAgencies);
 
  const handelSupplierSearch = async(input)=>{
    await getAgencyQuery(
        {fetchPolicy: 'cache-and-network',
            variables: {
                input: {
                    filters: {
                      advanceFilter: [
                        [{
                          operator: "suppliername",
                          condition: "and",
                          filter: "contain_exactly",
                          filterValue: [input]
                        }]
                      ]
                    },
                  page: 1,
                  pageSize: 5,
                }
            }
        }
    );
  };


  // Debounced version
  const debouncedSupplierSearch = useCallback(
    debounce((input) => {
        if(input?.length>2){
            handelSupplierSearch(input);
        }
    }, 500), // 500ms delay
    [] // dependencies
  );


  const [getEmployeeQuery] = useLazyQuery(GetEmployeeRecord);

  // kept for future employee search use; currently dispatch is in-house or supplier only
  useCallback(
    debounce((input) => {
        if(input?.length>2){
            getEmployeeQuery({ variables: { input: { filters: null, search: input, page: 1, pageSize: 5 } }, fetchPolicy: 'cache-and-network' });
        }
    }, 500), // 500ms delay
    [] // dependencies
  );

  const site = Form.useWatch("siteId", form);
  const scheduleType = Form.useWatch("scheduleType", form);
  const startTime = Form.useWatch("startTime", form);

  useEffect(()=>{
    if(startTime && dayjs(startTime).isValid()){
      form.setFieldValue("endTime", dayjs(startTime).add(1, "hour"));
    }
  },[startTime]);


  const {data: customerTemplateData, loading: customerTemplateLoading} = useQuery(getReportTemplates,{
      variables: {
          customerId: site? JSON.parse(site)?.customerId : null,
          isArchived: false
      },
      fetchPolicy: 'network-only',
      skip: !site
  });

  const {data: StandardcustomerTemplateData, loading: StandardcustomerTemplateLoading} = useQuery(getReportTemplates,{
      variables: {
          customerId: null,
          isArchived: false
      },
      fetchPolicy: 'network-only',
  });




    useEffect(()=>{
      if(site && JSON.parse(site)){
        form.setFieldValue("supplierId", JSON.parse(site)?.supplierId);
      }
      setLockedEmployee(null);
    },[site]);


    const [GetKeyCurrentCustodianBySiteId, {data:GetKeyCurrentCustodianBySiteIdData}] = useLazyQuery(GetKeyCurrentCustodianBySiteIdQuery)

    useEffect(()=>{
      if(site?.length>0){
        GetKeyCurrentCustodianBySiteId({
          variables:{
            siteId: JSON.parse(site)?.siteId
          },
          fetchPolicy: 'network-only'
        });
      }
    },[site]);

    useEffect(()=>{

      if(GetKeyCurrentCustodianBySiteIdData?.getKeyCurrentCustodianBySiteId?.response){
        const keyDetail = GetKeyCurrentCustodianBySiteIdData?.getKeyCurrentCustodianBySiteId?.response;
        console.log(keyDetail?.custodianType?.toLowerCase(), "keyDetail?.custodianType?.toLowerCase()");
        if(keyDetail?.custodianType?.toLowerCase()=="supplier"){
          // keygroup assigned to supllier but not match with current supplier
          if((site && JSON.parse(site) && JSON.parse(site)?.supplierId) &&  JSON.parse(site)?.supplierId!==keyDetail?.custodianId){
            Swal.fire({
              icon: "error",
              title: "Supplier and Key Mismatch",
              html: `
                <div style="text-align:left; font-size:14px; line-height:1.5;">
                  <p style="margin-bottom:12px;">
                    This action cannot continue because the selected supplier does not match the supplier linked to the assigned key.
                  </p>
            
                  <div style="background:#f8f9fa; padding:12px; border-radius:6px;">
                    <div style="margin-bottom:6px;">
                      <strong>Current Supplier:</strong> ${JSON.parse(site)?.currentsupplier}
                    </div>
                    <div>
                      <strong>Key Assigned To:</strong> ${keyDetail?.custodianDetail?.name}
                    </div>
                  </div>
                </div>
              `,
              confirmButtonText: "Got it",
              confirmButtonColor: "#d33",
            });
          }
          // site does not have the supplier but keygroup or key set already assigned to supplier
          else if((site && JSON.parse(site) && !JSON.parse(site)?.supplierId)){
            Swal.fire({
              icon: "error",
              title: "No Supplier Selected for This Site",
              html: `
                <div style="text-align:left; font-size:14px; line-height:1.5;">
                  <p style="margin-bottom:12px;">
                    No supplier is currently selected for this site. Please select a supplier and come back to schedule a task for this site.
                  </p>

                  <div style="background:#f8f9fa; padding:12px; border-radius:6px;">
                    <div>
                      <strong>Note:</strong> This site's key group is already assigned to <strong>${keyDetail?.custodianDetail?.name}</strong>.
                    </div>
                  </div>
                </div>
              `,
              confirmButtonText: "Got it",
              confirmButtonColor: "#d33",
            });
          }
        }
        // now supplier is on site but key is inhouse
        else if((site && JSON.parse(site) && JSON.parse(site)?.supplierId) &&  keyDetail?.custodianType && keyDetail?.custodianType?.toLowerCase()!=="supplier"){
          Swal.fire({
            icon: "error",
            title: "Supplier and Key Assignment Mismatch",
            html: `
              <div style="text-align:left; font-size:14px; line-height:1.5;">
                <p style="margin:0 0 12px 0;">
                  This action cannot continue because the site has a supplier assigned, but the selected key is currently assigned in-house.
                </p>
                <div style="background:#f8f9fa; border-radius:8px; padding:12px;">
                  <div style="margin-bottom:8px;"><strong>Site Supplier:</strong> ${JSON.parse(site)?.currentsupplier}</div>
                  <div><strong>Key Assignment:</strong> In-House</div>
                </div>
              </div>
            `,
            confirmButtonText: "Got it",
            confirmButtonColor: "#d33",
          });
        }
        else{
          if(keyDetail?.custodianType && keyDetail?.custodianType?.toLowerCase()=="in-house"){
            form.setFieldsValue({
              dispatchMethod:"employee"
            });
          }
          else if(keyDetail?.custodianType && keyDetail?.custodianType?.toLowerCase()=="employee"){
            form.setFieldsValue({
              dispatchMethod:"employee",
              employeeId: keyDetail?.custodianId
            });
            setLockedEmployee({ id: keyDetail?.custodianId, name: keyDetail?.custodianDetail?.name });
            Swal.fire({
              icon: "info",
              title: "Driver Already En Route",
              html: `
                <div style="text-align:left; font-size:14px; line-height:1.5;">
                  <p style="margin-bottom:12px;">
                    <strong>${keyDetail?.custodianDetail?.name}</strong> is already en route and currently holds the key for this site.
                  </p>
                  <div style="background:#f0f7ff; border-left:4px solid #1890ff; padding:12px; border-radius:4px;">
                    <strong>Note:</strong> This task will be strictly assigned to this driver. If you need to reassign, you can transfer the task after it has been created.
                  </div>
                </div>
              `,
              confirmButtonText: "Understood",
              confirmButtonColor: "#1890ff",
            });
          }
        }
      }

    },[GetKeyCurrentCustodianBySiteIdData?.getKeyCurrentCustodianBySiteId?.response]);

    // Selecting an already-Terminated/Termination Pending site doesn't wait for the submit
    // rejection (assertDutyCreationAllowed in mobileServices.service.ts's newMobileResponse is
    // still the actual enforcement) - it's called out the moment the site is picked, same as the
    // supplier/key mismatch checks above. Purely informational: dismissing leaves the site
    // selected, and the real block only kicks in on save.
    useEffect(()=>{
      if(!site) return;
      (async()=>{
        try{
          const selectedSiteId = JSON.parse(site)?.siteId;
          if(!selectedSiteId) return;
          const { data } = await fetchPrefillSite({ variables: { id: selectedSiteId } });
          const selectedSite = data?.site?.response;
          const status = String(selectedSite?.metadata?.sitestatus || "").trim();
          if(status==="Terminated" || status==="Termination Pending"){
            const endDate = selectedSite?.metadata?.contractenddate;
            const result = await Swal.fire({
              icon: "error",
              title: "Site Already Terminated",
              html: `
                <div style="text-align:left; font-size:14px; line-height:1.5;">
                  <p style="margin:0;">
                    <strong>${selectedSite?.sitename}</strong> was ${status==="Termination Pending" ? "set to Termination Pending" : "terminated"}${endDate ? ` on <strong>${dayjs.utc(endDate).format("DD/MM/YYYY")}</strong>` : ""}.
                    New duties can't be scheduled for this site.
                  </p>
                </div>
              `,
              showCancelButton: true,
              confirmButtonText: "View Site Detail",
              cancelButtonText: "Dismiss",
              confirmButtonColor: "#0D5C63",
              cancelButtonColor: "#6c757d",
            });
            if(result.isConfirmed){
              window.open(`/user/site-detail/${selectedSiteId}/tab/operations`, "_blank");
            }else{
              close()
            }
          }
        }catch(e){
          console.error("Site termination check failed", e);
        }
      })();
    },[site]);

    const [customScheduleModel, setCustomScheduleModel] = useState(false);
    // Tracks whether the UFN modal was already open on the *previous* commit, so the
    // reset-on-empty-close check below only reacts to a genuine open->close transition -
    // never to the same-tick render where scheduleType just flipped to "ufn" but the
    // setCustomScheduleModel(true) from the effect above hasn't been applied yet (that
    // stale read used to make this effect wipe scheduleType back out immediately).
    const wasCustomScheduleModelOpen = useRef(false);

    useEffect(()=>{
      if(scheduleType=="ufn"){
        setCustomScheduleModel(true);
      }
    },[scheduleType])

    useEffect(()=>{
      if(wasCustomScheduleModelOpen.current && !customScheduleModel && scheduleType=="ufn" && scheduleDays?.length==0 && frequencyDays==0){
        form.resetFields(["scheduleType"]);

      }
      wasCustomScheduleModelOpen.current = customScheduleModel;
    },[scheduleType, scheduleDays, frequencyDays, customScheduleModel]);


    

  return (
    <>


      <CustomScheduleModal
        visible={customScheduleModel}
        onClose={()=>setCustomScheduleModel(false)}
        title={"Until Further Notice"}
        scheduleDays={scheduleDays}
        setScheduleDays={setScheduleDays}
        frequencyDays={frequencyDays}
        setFrequencyDays={setFrequencyDays}
        form={form}
      />

      {/* Follow-up from the "do you have a key yet?" soft check above - said yes, so opens the New
          Key form prefilled to that site once this modal's own save has already gone through. */}
      {postSaveKeyCheckSite && (
        <NewKeyModal
          visible={!!postSaveKeyCheckSite}
          prefillSiteId={postSaveKeyCheckSite._id}
          prefillSiteName={postSaveKeyCheckSite.sitename}
          close={()=>setPostSaveKeyCheckSite(null)}
        />
      )}


      <Modal
        open={visible}
        width={1000}
        style={{ top: 10, overflow: "auto" }}
        footer={
          <div
            className="drawer-footer"
            style={{
              padding: "26px 40px",
              textAlign: "left",
              display: "flex",
              columnGap: "16px",
              marginTop: "-25px",
            }}
          >
            <button className={newMobileResponseLoading? "drawer-filled-btn disabled-btn":"drawer-filled-btn"} onClick={handleSave} >Save {newMobileResponseLoading && <Spinner /> } </button>
            <button className={newMobileResponseLoading? "drawer-outlined-btn disabled-btn" :"drawer-outlined-btn"} onClick={()=>{ 
              form.resetFields(); 
              close();
            }}>
              Cancel
            </button>
          </div>
        }
        closable={false}
      >
        <>
          <div className="modal-header-title">
            <span>New Response Request &nbsp;<HelpLink slug="ops-mobile-services" /></span>
            <span onClick={()=>{ form.resetFields(); 
              close();
            }}>
              <FontAwesomeIcon className="close" icon={faClose} />
            </span>
          </div>

          <div className="modal-body">
            <Form
              form={form}
              layout="vertical"
              // onFinish={(e) => console.log(e)}
              requiredMark={false}
              className="mobileResponseForm"
              initialValues={{
                dispatchMethod: "supplier",
                payRate: "site",
                billRate: "site"
              }}
            >

              <Row gutter={16}>
               
                <Col xs={24} md={12}>
                  <Form.Item
                    name="siteId"
                    label="Site"
                    rules={[
                      { required: true, message: "Please select a site" },
                    ]}
                    
                  >
                    <Select
                      className="custom-select"
                      placeholder="Type to search site..."
                      showSearch
                      suffixIcon={<CaretDownFilled />}
                      onSearch={debouncedSiteSearch}
                      loading={siteLoading}
                      filterOption={false} 
                    >
                      {siteLoading ? (
                          <Option>
                              <div style={{display:'flex', justifyContent:'center'}}>
                                  <Spinner/>
                              </div>
                          </Option>
                      ) : (
                          <>
                            {/* Prefilled site option — always visible even before user searches */}
                            {prefillSiteOption && (
                              <Option value={prefillSiteOption.value}>
                                <div style={{display:'flex', gap:'0', flexDirection:'column'}}>
                                  <span style={{lineHeight:'20px'}}>{prefillSiteOption.label}</span>
                                  {prefillSiteOption.postcode && (
                                    <span style={{lineHeight:'10px', color:'#9ca3af'}}>{prefillSiteOption.postcode}</span>
                                  )}
                                </div>
                              </Option>
                            )}
                            {siteData?.sites?.sites
                              ?.filter(s => !prefillSiteOption || s._id !== JSON.parse(prefillSiteOption.value).siteId)
                              ?.map((site) => (
                                <Option value={JSON.stringify({siteId: site?._id, customerId: site?.customerDetail?._id, supplierId: site?.metadata?.supplierId, currentsupplier: site?.metadata?.currentsupplier})}>
                                  <div style={{display:'flex', gap:'0', flexDirection:'column'}}>
                                    <span style={{lineHeight:'20px'}}>{site?.sitename} &nbsp;
                                      {site?.keyDetail?.length ? <FontAwesomeIcon color='#9ca3af' icon={faKey}/> : null}
                                    </span>
                                    <span style={{lineHeight:'10px', color:'#9ca3af'}}>{site?.postcode}</span>
                                  </div>
                                </Option>
                              ))
                            }
                          </>
                      )}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    rules={[
                      { required: true, message: "Please select priority" },
                    ]}
                  >
                      <Select
                          className="custom-select"
                          placeholder="Type to search priority..."
                          showSearch
                          suffixIcon={<CaretDownFilled />}
                      >
                          <Option value="high">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                              style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: "#FF4D4F",
                              }}
                              />
                              <span>High</span>
                          </div>
                          </Option>
                      
                          <Option value="medium">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                              style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: "#FA8C16",
                              }}
                              />
                              <span>Medium</span>
                          </div>
                          </Option>
                      
                          <Option value="low">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                              style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: "#52C41A",
                              }}
                              />
                              <span>Low</span>
                          </div>
                          </Option>
                      </Select>
                

                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>

              <Col xs={24} md={12}>
                  <Form.Item
                    name="dutyTypeId"
                    label="Duty Type"
                    rules={[
                      { required: true, message: "Please select a duty type" },
                    ]}
                  >
                    <Select
                      className="custom-select"
                      placeholder="Type to search duty types..."
                      showSearch
                      optionFilterProp="children"
                      suffixIcon={<CaretDownFilled />}
                      loading={dutyTypeLoading}
                      disabled={dutyTypeLoading}
                      // the company's report form for this job type (Settings > Mobile Services)
                      onChange={(dutyTypeId) => {
                        const formId = reportFormsData?.mobileReportForms?.jobTypes?.find((t) => t._id === dutyTypeId)?.reportTemplateId;
                        if (formId && !form.getFieldValue("reportTemplateId")) form.setFieldsValue({ reportTemplateId: formId });
                      }}
                    >
                      {/* mobile jobs: only duty types in the Mobile category */}
                      {dutyTypeData?.getPayLevel?.response?.filter((dutyType) => dutyType?.category === "mobile")?.map((dutyTypeData) => (
                          <Option value={dutyTypeData?._id}>{dutyTypeData?.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="dispatchMethod"
                    label="Dispatch Method"
                    rules={[
                      { required: true, message: "Please select dispatch method" },
                    ]}
                  >
                      <Select
                          className="custom-select"
                          placeholder="Select Dispatch Method"
                          showSearch
                          suffixIcon={<CaretDownFilled />}
                          disabled={!!lockedEmployee}
                      >
                          <Option value={"employee"}>In-House Mobile Officer</Option>
                          <Option value={"supplier"}>Supplier</Option>
                      </Select>
                  </Form.Item>
                </Col>
              </Row>

              
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="reportTemplateId"
                    label="Report Template"
                    rules={[
                      { required: true, message: "Please select a report template" },
                    ]}
                  >
                    <Select
                      className="custom-select"
                      placeholder="Select a report template..."
                      showSearch
                      suffixIcon={<CaretDownFilled />}
                      disabled={!site}
                      loading={customerTemplateLoading || StandardcustomerTemplateLoading}

                    >
                      <OptGroup label={"Standard Template"}>
                      {
                          StandardcustomerTemplateData?.getReportTemplates?.response?.map((temp)=>(
                              <Option value={temp?._id}>{temp?.templatename}</Option>
                          ))
                      }
                      </OptGroup>
                      

                      <OptGroup label="Custom Template">
                          {
                              customerTemplateData?.getReportTemplates?.response?.map((temp)=>(
                                  <Option value={temp?._id}>{temp?.templatename}</Option>
                              ))
                          }
                      </OptGroup>


                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>

                  {/* Conditional Field */}
                  <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                      prev.dispatchMethod !== curr.dispatchMethod
                      }
                  >
                      {({ getFieldValue, resetFields }) => {
                      const method = getFieldValue("dispatchMethod");

                      if (!method) return null;

                      // Reset opposite field safely
                      if (method === "employee") {
                          resetFields(["supplierId"]);
                      } else {
                          resetFields(["employeeId"]);
                      }

                      return method === "employee" ? (
                          lockedEmployee ? (
                          <>
                          <Form.Item name="employeeId" noStyle><Input type="hidden" /></Form.Item>
                          <Form.Item
                              label="Mobile Team"
                          >
                              <Input
                                  value={lockedEmployee.name}
                                  readOnly
                                  className="generic-input-control"
                                  style={{ cursor: "default", backgroundColor: "#f5f5f5", color: "#595959" }}
                              />
                          </Form.Item>
                          </>
                          ) : (
                          <Form.Item
                              name="employeeId"
                              label="Mobile Driver"
                              tooltip="Direct-jobs drivers. Leave it empty for the In-House Mobile Team: the officer on shift holding the site's keys gets it, or it waits Open."
                          >
                              <Select
                                  className="custom-select"
                                  showSearch
                                  allowClear
                                  optionFilterProp="label"
                                  placeholder="In-House Mobile Team"
                                  suffixIcon={<CaretDownFilled />}
                                  loading={directDriversLoading}
                                  options={(directDriversData?.directMobileDrivers || []).map((d) => ({ value: d._id, label: d.name }))}
                                  notFoundContent="No direct-jobs drivers yet: set one up under Employee › Employee App Access › Mobile Driver"
                              />
                          </Form.Item>
                          )
                      ) : (
                          <Form.Item
                              name="supplierId"
                              label="Supplier"
                              rules={[{ required: true, message: "Please select a supplier" }]}
                          >
                              <Select
                                  className="custom-select"
                                  showSearch
                                  placeholder="Select a supplier..."
                                  suffixIcon={<CaretDownFilled />}
                                  onSearch={debouncedSupplierSearch}
                                  filterOption={false}
                                  loading={agencyLoading}
                              >
                                  {agencyLoading ?
                                      <Option>
                                          <div style={{display:'flex', justifyContent:'center'}}>
                                              <Spinner />
                                          </div>
                                      </Option>
                                      :
                                      <>
                                      {
                                        site && JSON.parse(site) && JSON.parse(site)?.supplierId &&
                                          <Option value={JSON.parse(site)?.supplierId}>
                                            <div style={{display:'flex', justifyContent:'space-between'}}>
                                              {JSON.parse(site)?.currentsupplier}
                                              <Tag style={{height:'20px'}}> Current Supplier </Tag>
                                            </div>
                                          </Option>
                                      }
                                      {
                                          agencyData?.agencies?.agencies?.map((agency)=>(
                                              <Option value={agency?._id}>{agency?.suppliername}</Option>
                                          ))
                                      }
                                      </>
                                  }
                              </Select>
                          </Form.Item>
                      );
                      }}
                  </Form.Item>

                </Col>
                
              </Row>


              
              <Row gutter={16}>

                <Col xs={24} md={12}>
                 
                    <Form.Item
                      name="scheduleType"
                      label={
                        <div style={{display:'flex', width:'100%'}}>
                          <span>
                            Schedule Frequency &nbsp;
                          </span>
                          {
                            scheduleType=="ufn" &&
                            <div style={{cursor:'pointer'}}
                            className="link"
                              onClick={()=>setCustomScheduleModel(true)}
                            >
                              
                              {
                                scheduleDays?.length>0 ? 
                                " ( " +
                                scheduleDays
                                ?.map(sd => {
                                  return sd?.charAt(0).toUpperCase() + sd?.slice(1);
                                })
                                ?.join(" ")
                                +" )"

                                
                                :
                                " ( Repeat After Every " + frequencyDays + " days )"
                              }
                            </div>
                          }
                        </div>
                      }
                      rules={[
                        { required: true, message: "Please select a schedule frequency" },
                      ]}
                      // className="scheduleTypeSearch"
                      // style={{width:'100%'}}
                    >
                      <Select
                          className="custom-select"
                          placeholder="Select schedule frequency"
                          suffixIcon={<CaretDownFilled />}
                          // value={scheduleType}
                      >

                        <Option value="one-off">One-off </Option>
                        <Option value="ufn">
                          <span style={{ display: "flex", justifyContent: "space-between" }}>
                            Until further notice
                            <FontAwesomeIcon
                              icon={faExternalLink}
                              
                            />
                          </span>
                        </Option>

                      </Select>

                    </Form.Item>

      

                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                      prev.dispatchMethod !== curr.dispatchMethod
                      }
                  >
                      {({ getFieldValue }) => {

                          const method = getFieldValue("dispatchMethod");
                          
                          return(
                              <Form.Item
                                  name="payRate"
                                  label="Pay Rate"
                                  rules={[
                                      { required: true, message: "Please select pay rate" },
                                  ]}
                              >
                                  

                                  <Select
                                      placeholder="Please select pay rate"
                                      className="custom-select"
                                      suffixIcon={<CaretDownFilled/>}
                                      disabled={method === "employee"}
                                  >
                                      {
                                          method=="employee"?
                                          Object.entries(empPay)?.map(([key, label])=>(
                                              <Option value={key}>{label}</Option>
                                          ))
                                          :
                                          Object.entries(supplierPay)?.map(([key, label])=>(
                                              <Option value={key}>{label}</Option>
                                          ))
                                      }


                                  </Select>
                              
                              </Form.Item>
                          )
                      
                      }}
                  </Form.Item>

                </Col>
              </Row>


              
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="startTime"
                    label="Start Time"
                    rules={[
                      { required: true, message: "Please enter start time" },
                    ]}
                  >

                      <DatePicker
                          showTime
                          className="generic-input-control"
                          placeholder="Task start time"
                          format={"DD/MM/YYYY HH:mm"}
                      />
                    
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="billRate"
                    label="Bill Rate"
                    rules={[
                      { required: true, message: "Please select Bill Rate" },
                    ]}
                  >
                    <Select
                      className="custom-select"
                      placeholder="Type to search site..."
                      showSearch
                      suffixIcon={<CaretDownFilled />}
                    >
                      {
                          Object.entries(billRate)?.map(([key,label])=>(
                              <Option value={key}> {label} </Option>
                          ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>


              
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="endTime"
                    label="End Time"
                    rules={[
                      { required: true, message: "Please enter end time" },
                    ]}
                  >
                    <DatePicker
                      showTime
                      className="generic-input-control"
                      placeholder="Task end time"
                      format={"DD/MM/YYYY HH:mm"}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="notes"
                    label="Additional Notes"
                    rules={[
                      { required: false, message: "Please enter additional notes" },
                    ]}
                  >
                    <Input
                      className="generic-input-control"
                      placeholder="Additional Notes"
                    />
                  </Form.Item>
                </Col>
              </Row>

            </Form>
          </div>
        </>
      </Modal>

      

    </>
  )
}


export const CustomScheduleModal = ({visible, onClose, title, scheduleDays, setScheduleDays, frequencyDays, setFrequencyDays})=>{
  const [selectedDays, setSelectedDays] = useState([]);
  const [repeatDays, setRepeatDays] = useState('');

  const handleDayChange = (checkedValues) => {
    setSelectedDays(checkedValues);
    if (checkedValues.length > 0) {
      setRepeatDays(''); // Clear input when any day is selected
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setRepeatDays(value);
    if (value) {
      setSelectedDays([]); // Clear all selected days when input has value
    }
  };

  useEffect(()=>{
    setScheduleDays(selectedDays);
  },[selectedDays]);

  useEffect(()=>{
    setFrequencyDays(repeatDays);
  },[repeatDays]);

  return(
    <Modal
      open={visible}
      width={650}
      style={{borderRadius:'3px'}}
      footer={
        <div className="drawer-footer"
          style={{
            padding: "26px 40px",
            textAlign: "left",
            display: "flex",
            columnGap: "16px",
          }}
        >
          <button disabled={false} className={'drawer-filled-btn'} onClick={onClose}>Save</button>
          <button disabled={false} className={'drawer-outlined-btn'} onClick={()=>{
            if(scheduleDays?.length==0 && frequencyDays==null){
              onClose();

            }else{

              onClose();
            }

          }}>Cancel</button>
        </div>
      }
      closable={false}
    >
      <React.Fragment>
        <div className='modal-header-title'>
          <span className=''>
            {title}
          </span>
          <span onClick={onClose}><FontAwesomeIcon className='close' icon={faClose}/></span>
        </div>

        <div className='modal-body'>
          <b> Every </b>

          <Checkbox.Group 
            value={scheduleDays}
            onChange={handleDayChange}
            style={{fontWeight:'500', display:'flex', gap:'20px', flexDirection:'row', margin:'16px 0'}}
          >
            <Checkbox value="mon"> Mon</Checkbox>
            <Checkbox value="tue"> Tue</Checkbox>
            <Checkbox value="wed"> Wed</Checkbox>
            <Checkbox value="thu"> Thu</Checkbox>
            <Checkbox value="fri"> Fri</Checkbox>
            <Checkbox value="sat"> Sat</Checkbox>
            <Checkbox value="sun"> Sun</Checkbox>
          </Checkbox.Group>
        </div>

        <div style={{display:'flex', flexDirection:'row', alignItems:'center', padding: '0 12px'}}>
          <div style={{borderBottom: '1px solid #D3D3D3', width: '88%'}}/>
          <span style={{margin:'0 12px'}}>OR</span>
          <div style={{borderBottom: '1px solid #D3D3D3', width: '88%'}}/>
        </div>

        <div className="modal-body">
          <label>
            Repeat after every
          </label>
          <div>
            <Input 
              className="generic-input-control"
              placeholder="Number of days"
              value={frequencyDays}
              onChange={handleInputChange}
              type="number"
              min={1}
              max={364}
            />
          </div>
        </div>
      </React.Fragment>
    </Modal>
  )
}